import CircularProgress from "@mui/material/CircularProgress";
import useTheme from "@mui/material/styles/useTheme";
import differenceInMinutes from "date-fns/differenceInMinutes";
import React, { lazy, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Route, Switch } from "react-router-dom";
import { Header } from "../components/Header";
import { getSecrets } from "../config";
import { useAppDispatch, useAppSelector } from "../hooks";
import { useIdentity } from "../hooks/useIdentity";
import useKeypairState from "../hooks/useKeypairState";
import { useRouter } from "../hooks/useRouter";
import { ROUTES } from "../routes";
import { setAssets } from "../store/reducers/asset";
import { setUsingLang } from "../store/reducers/settings";
import { loadState, saveState } from "../utils/localStorage.utils";
import { LocalStorageKeys } from "../utils/strings.utils";
import "./App.css";
import { useWhiteboardRoom } from "../hooks/useWhiteboardRoom";
import { useLoader } from "../hooks/useLoader";
import { useViewport } from "../hooks/useViewport";
import { useGuestDataLinking } from "../hooks/useGuestDataLinking";
import PWAInstallSheet from "../components/PWAInstallSheet";

const StaleModal = lazy(() => import("./StaleModal"));
const NotificationModal = lazy(() => import("./NotificationModal"));
const KeypairPage = lazy(() => import("./KeypairPage"));
const FriendPage = lazy(() => import("./FriendPage"));
const SmallAppsPage = lazy(() => import("./SmallAppsPage"));
const DMPage = lazy(() => import("./DMPage"));
const BtoCPage = lazy(() => import("./BtoCPage"));
const WhiteBoard = lazy(() => import("./WhiteBoard"));
const LoginPage = lazy(() => import("./LoginPage"));
const App = () => {
  const dispatch = useAppDispatch();
  const { i18n, t } = useTranslation();
  const {
    isAdmin,
    isFromExtension,
    isLoading: isLoadingIdentity,
  } = useIdentity();
  const { isInvited } = useAppSelector((state) => state.auth);
  const { NODE_ENV } = getSecrets();
  const isInIframe = window.location !== window.parent.location;
  const theme = useTheme();
  const {
    query,
    pathname,
  }: { query: { [key: string]: string }; pathname: string } = useRouter();
  const { isAppOpened, isStale } = useViewport();
  const { roomError, logoUrl, isLoadingImage, backgroundImageUrl } =
    useWhiteboardRoom();
  const isAppLoading = isLoadingIdentity || isLoadingImage || !isAppOpened;
  const userAgent = navigator.userAgent; // Detect user's browser
  const { isLoading, longRespondStatus } = useLoader(isAppLoading, "app");
  useKeypairState();

  // Handle guest data linking based on user choice after login
  useGuestDataLinking();
  useEffect(() => {
    dispatch(setUsingLang(i18n.language));
  }, []);

  useEffect(() => {
    const assetLastUpdateTime = new Date(
      loadState(LocalStorageKeys.ASSET_LAST_UPDATE_TIME)
    );
    if (
      assetLastUpdateTime &&
      differenceInMinutes(new Date(), assetLastUpdateTime) > 10
    ) {
      saveState(LocalStorageKeys.ASSET_LIST, {});
      saveState(LocalStorageKeys.ASSET_LAST_UPDATE_TIME, new Date());
    } else {
      const assetList = loadState(LocalStorageKeys.ASSET_LIST);
      dispatch(setAssets(assetList ?? {}));
    }

    let intervalId = setInterval(() => {
      saveState(LocalStorageKeys.ASSET_LIST, {});
      saveState(LocalStorageKeys.ASSET_LAST_UPDATE_TIME, new Date());
      dispatch(setAssets({}));
    }, 600000);

    return () => clearInterval(intervalId);
  }, []);

  const isForbid = () => {
    if (isAppLoading) return false;
    if (isInIframe || isFromExtension) return false; // Make sure users are from iframe or our extension
    // @ts-ignore
    if (typeof query.roomUrl !== "undefined") return false;
    if (NODE_ENV !== "production") return false;
    if (isAdmin) return false;
    return true;
  };
  const appview = () => {
    if (isAppLoading)
      return (
        <div className="logo-imageContainer">
          {logoUrl != undefined ? (
            <img className="logo-image" src={logoUrl} />
          ) : (
            <CircularProgress
              className="logo-image"
              style={{ color: theme.brandColor }}
              size={20}
            />
          )}
        </div>
      );
    /** if not in iframe (ex: in extension) and not invited */
    //if (!isInIframe && !isInvited) return <RedeemVoucherPage />;
    return (
      <>
        <div className="App-container">
          <Switch>
            <Route path={ROUTES.WHITEBOARD_PAGE} exact>
              <WhiteBoard />
            </Route>
            <Route path={ROUTES.STATIC_HOME_PAGE} exact>
              <WhiteBoard />
            </Route>
            <Route path={ROUTES.LOGIN_PAGE} exact>
              <LoginPage />
            </Route>
            <Route path={ROUTES.FRIEND_PAGE} component={FriendPage} exact />
            <Route path={ROUTES.KEYPAIR_PAGE} component={KeypairPage} exact />
            <Route
              path={[ROUTES.DM_PAGE + "/:toAddress", ROUTES.DM_PAGE]}
              component={DMPage}
            />
            <Route
              path={[ROUTES.B_TO_C_PAGE + "/:toDomain", ROUTES.B_TO_C_PAGE]}
              component={BtoCPage}
              exact
            />
            <Route
              path={ROUTES.SMALL_APPS_PAGE}
              component={SmallAppsPage}
              exact
            />
          </Switch>
        </div>
      </>
    );
  };
  return (
    <div className="App" role="presentation">
      {backgroundImageUrl && (
        <div
          className="custom-background"
          style={{ backgroundImage: `url(${backgroundImageUrl})` }}
        ></div>
      )}
      {isForbid() ? (
        <>
          <Header title={"Protico"} />
          <div className="forbid-App">
            <p>{t("Alert_Lobby_Invalid")}</p>
            <p>{t("Alert_Wrong_Lobby_Info")}</p>
            <p>{t("Alert_Contact_Team")}</p>
          </div>
        </>
      ) : !!roomError ? (
        userAgent === "Firefox" ? (
          <>
            <Header title={"Protico"} />
            <div className="forbid-App">
              <p>{t("Alert_Lobby_Firefox_Error")}</p>
              <p>{t("Alert_Wrong_Firefox_Info")}</p>
              <p>{t("Alert_Change_Browser")}</p>
            </div>
          </>
        ) : (
          <>
            <Header title={"Protico"} />
            <div className="forbid-App">
              <p>{t("Alert_Lobby_System_Wrong")}</p>
              <p>{t("Alert_Wrong_Lobby_Info")}</p>
              <p>{t("Alert_Contact_Team")}</p>
            </div>
          </>
        )
      ) : (
        <>
          <Header title={"Protico"} />
          <PWAInstallSheet />
          {appview()}
          {isLoading && (
            <div className="App-loader-container">
              {logoUrl != undefined ? (
                <div
                  style={{
                    height: "60px",
                    maxWidth: "190px",
                  }}
                >
                  <img className="logo-image" src={logoUrl} />
                </div>
              ) : (
                <CircularProgress
                  className="logo-image"
                  style={{ color: theme.brandColor }}
                  size={20}
                />
              )}
              {isStale ? (
                <StaleModal />
              ) : longRespondStatus ? (
                <div
                  className="App-loader-container-text-div"
                  style={{ textAlign: "left" }}
                >
                  <p className="App-loader-container-text">
                    {t("Loading_LongRespond_Title")}
                  </p>
                  <ol>
                    <li className="App-loader-container-text-list">
                      {t("Loading_LongRespond_3")}
                    </li>
                  </ol>
                </div>
              ) : (
                <div className="App-loader-container-text-div">
                  <p className="App-loader-container-text">
                    {t("FlashChat_LoadingPage_Desc")}
                  </p>
                </div>
              )}
            </div>
          )}
          {!isLoading && <NotificationModal />}
        </>
      )}
    </div>
  );
};
export default App;
